import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { emptyNote, SOAP_KEYS } from "@/lib/soap";

const FIELDS = Object.keys(emptyNote());
const payloadOf = (f) => Object.fromEntries(FIELDS.map((k) => [k, f[k]]));
const snapshot = (f) => JSON.stringify(payloadOf(f));
const hasContent = (f) =>
  !!f.client_id || !!f.session_details?.trim() || SOAP_KEYS.some((s) => f[s.key]?.trim()) ||
  f.targets?.some((t) => t.total) || f.behaviors?.some((b) => b.frequency) ||
  Object.values(f.template_fields || {}).some((v) => v?.trim());

export function useAutosave(form, setForm, initialId, paused, onCreated) {
  const idRef = useRef(initialId);
  const checkpointRef = useRef("");
  const lastRef = useRef(initialId ? null : snapshot(form));
  const savingRef = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;
  const mountedRef = useRef(true);
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);

  const markClean = useCallback((f) => { lastRef.current = snapshot(f); setStatus("idle"); }, []);
  const dirty = () => lastRef.current !== null && snapshot(formRef.current) !== lastRef.current &&
    (!!idRef.current || hasContent(formRef.current));

  const save = useCallback(async ({ force = false, checkpoint = "" } = {}) => {
    if (savingRef.current) await savingRef.current.catch(() => {});
    const f = formRef.current;
    const body = payloadOf(f);
    const json = JSON.stringify(body);
    if (!force && (json === lastRef.current || (!idRef.current && !hasContent(f)))) return null;
    setStatus("saving");
    const cp = checkpoint || checkpointRef.current;
    checkpointRef.current = "";
    const req = idRef.current ? api.put(`/notes/${idRef.current}`, body, { params: cp ? { checkpoint: cp } : {} }) : api.post("/notes", body);
    const p = req.then(({ data }) => {
      lastRef.current = json;
      setSavedAt(new Date());
      setStatus(snapshot(formRef.current) === json ? "saved" : "unsaved");
      if (!idRef.current) {
        idRef.current = data.id;
        if (mountedRef.current) {
          setForm((x) => ({ ...x, id: data.id }));
          onCreated?.(data.id);
        }
      }
      return data;
    });
    savingRef.current = p;
    try { return await p; } catch (e) { setStatus("error"); throw e; } finally { savingRef.current = null; }
  }, [setForm, onCreated]);

  useEffect(() => {
    if (paused || lastRef.current === null || snapshot(form) === lastRef.current) return;
    if (!idRef.current && !hasContent(form)) return;
    setStatus((s) => (s === "saving" || s === "error" ? s : "unsaved"));
    const t = setTimeout(() => save().catch(() => {}), status === "error" ? 10000 : 2500);
    return () => clearTimeout(t);
  }, [form, paused, save, status]);

  useEffect(() => {
    const flush = (e) => {
      if (!dirty()) return;
      const p = save().catch(() => {});
      e?.detail?.pending?.push(p);
    };
    const beforeUnload = (e) => { if (dirty()) { flush(); e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("bb-flush", flush);
    window.addEventListener("beforeunload", beforeUnload);
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.removeEventListener("bb-flush", flush);
      window.removeEventListener("beforeunload", beforeUnload);
      flush();
    };
  }, [save]); // eslint-disable-line react-hooks/exhaustive-deps

  return { save, markClean, status, savedAt, checkpointRef };
}
